@if (session(); as s) {
  <!-- z-[70] keeps this tour above every nf-dialog tier (50, 60 and 65), so it is never covered by a dialog. -->
  <div
    class="nf-wn fixed inset-0 z-[70] flex items-center justify-center focus:outline-none"
    role="dialog"
    aria-modal="true"
    [attr.aria-labelledby]="titleId"
    tabindex="-1"
    cdkTrapFocus
    [style]="eventVars()"
  >
    <!-- Pointer-only: keyboard users close with Escape or the close button. -->
    <button
      type="button"
      tabindex="-1"
      class="nf-wn-backdrop absolute inset-0 bg-black/60 backdrop-blur-sm focus:outline-none"
      [attr.aria-label]="text.close | transloco"
      (click)="onBackdrop()"
    ></button>

    @if (showIntro()) {
      <!-- Entrance animation: plays once per session, ahead of the real card -->
      <div class="relative flex flex-col items-center gap-4 pointer-events-none" aria-live="polite">
        @if (introComponent(); as intro) {
          <ng-container [ngComponentOutlet]="intro" />
        } @else {
          <h2
            [id]="titleId"
            class="nf-wn-intro-title text-5xl sm:text-6xl font-bold tracking-tight text-white text-center"
          >
            {{ text.title | transloco }}
          </h2>
          <div class="nf-wn-intro-line w-56 sm:w-72 h-0.5"></div>
          <p
            class="nf-wn-intro-subtitle text-sm tracking-[0.26em] uppercase font-mono px-3 py-1 rounded-md text-white/70 bg-white/10 border border-white/20"
          >
            {{ text.introSubtitle | transloco }}
          </p>
        }
      </div>
    } @else {
      <div
        class="nf-wn-card relative w-full max-w-4xl h-[600px] max-h-[85vh] m-4 flex rounded-2xl overflow-hidden shadow-2xl bg-sk-surface border border-sk-border"
      >
        @if (decorationComponent(); as decoration) {
          <div class="absolute inset-0 pointer-events-none" aria-hidden="true">
            <ng-container [ngComponentOutlet]="decoration" />
          </div>
        }

        <!-- Sidebar: one row per release -->
        <aside
          class="w-[260px] shrink-0 hidden sm:flex flex-col gap-1 p-6 bg-sk-surface-raised border-r border-sk-border overflow-y-auto"
        >
          <p class="text-xs font-semibold uppercase tracking-wide text-sk-text-secondary mb-3">
            {{ text.title | transloco }}
          </p>
          <nav class="flex flex-col gap-1">
            @for (release of s.releases; track release.id; let i = $index) {
              <button
                type="button"
                class="nf-wn-release flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors"
                [class.nf-wn-release--active]="i === s.releaseIndex"
                [attr.aria-current]="i === s.releaseIndex ? 'true' : null"
                (click)="whatsNew.selectRelease(i)"
              >
                <span class="flex-1 truncate">{{ release.title }}</span>
                @if (!whatsNew.isSeen(release.id)) {
                  <span
                    class="nf-wn-unread w-2 h-2 rounded-full shrink-0"
                    [attr.aria-label]="text.unread | transloco"
                  ></span>
                }
              </button>
            }
          </nav>
          <button
            type="button"
            class="mt-auto pt-4 text-left text-xs text-sk-text-secondary hover:text-sk-text transition-colors"
            (click)="close()"
          >
            {{ text.skip | transloco }}
          </button>
        </aside>

        <!-- Active step -->
        <section class="relative flex-1 flex flex-col min-w-0">
          <button
            type="button"
            class="absolute top-4 right-4 z-10 p-2 rounded-full text-sk-text-secondary hover:text-sk-text hover:bg-sk-surface-raised transition-colors"
            [attr.aria-label]="text.close | transloco"
            (click)="close()"
          >
            <sk-icon name="close" size="sm" />
          </button>

          @if (activeItem(); as item) {
            <div class="flex-1 overflow-hidden p-8 flex items-center justify-center">
              @if (item.illustration) {
                <ng-container [ngComponentOutlet]="item.illustration" />
              }
            </div>
            <div class="px-8 pb-6 shrink-0 flex flex-col gap-2">
              <p class="text-xs font-mono uppercase tracking-wide text-sk-text-secondary sm:hidden">
                {{ activeRelease()?.title }}
              </p>
              <h3 [id]="titleId" class="text-lg font-semibold text-sk-text">{{ item.title }}</h3>
              <p class="text-sm text-sk-text-secondary">{{ item.description }}</p>
              @if (item.ctaUrl) {
                <button
                  type="button"
                  class="self-start inline-flex items-center gap-1 text-sm font-medium text-sk-primary hover:underline"
                  (click)="openCta(item.ctaUrl)"
                >
                  {{ item.ctaLabel ?? (text.learnMore | transloco) }}
                  <sk-icon name="external-link" size="xs" />
                </button>
              }
            </div>
          }

          <div
            class="flex items-center justify-between gap-4 px-8 py-4 border-t border-sk-border shrink-0"
          >
            <div class="flex gap-1.5" role="tablist">
              @for (step of activeRelease()?.items ?? []; track step.id; let i = $index) {
                <button
                  type="button"
                  role="tab"
                  class="nf-wn-dot w-1.5 h-1.5 rounded-full transition-colors"
                  [class.nf-wn-dot--active]="i === s.stepIndex"
                  [attr.aria-selected]="i === s.stepIndex"
                  [attr.aria-label]="step.title"
                  (click)="whatsNew.selectStep(i)"
                ></button>
              }
            </div>

            <div class="flex items-center gap-2">
              <button
                type="button"
                class="p-2 rounded-lg text-sk-text-secondary hover:text-sk-text hover:bg-sk-surface-raised transition-colors disabled:opacity-30 disabled:pointer-events-none"
                [disabled]="whatsNew.isFirstStep()"
                [attr.aria-label]="text.prev | transloco"
                (click)="whatsNew.prev()"
              >
                <sk-icon name="chevron-left" size="sm" />
              </button>
              <sk-button variant="primary" size="sm" (clicked)="whatsNew.primary()">
                @if (primaryLabelKey(); as labelKey) {
                  {{ labelKey | transloco }}
                }
              </sk-button>
            </div>
          </div>
        </section>
      </div>
    }
  </div>
}
